/**
 * Money on screen.
 *
 * TWO RULES, BOTH LOAD-BEARING.
 *
 * 1. A NEGATIVE FIGURE IS SHOWN AS NEGATIVE. `PaymentsView.availableMinor` "may be negative" — a
 *    refunded tip is a real deduction — and the design says in terms that it is NEVER CLAMPED. So
 *    there is no `Math.abs` here and no floor at zero. What there is: `negativeReason` printed
 *    beside it, "present so the number is explained rather than concealed".
 *
 * 2. INTEGER MINOR UNITS, DIVIDED ONCE, AT THE EDGE. Every amount on this surface is
 *    `MinorUnits` — an integer — with a `CurrencyCode` beside it. The division happens in
 *    ../format.ts and nowhere else, and never before an addition.
 */
import { Text } from '@mantine/core';
import type { TextProps } from '@mantine/core';
import { formatMoney } from '../format';

export interface MoneyProps extends Omit<TextProps, 'children'> {
  minor: number;
  currency: string;
  /** Colour a negative figure with the danger token. Off by default — most figures are neutral. */
  emphasiseNegative?: boolean;
}

export function Money({ minor, currency, emphasiseNegative = false, ...textProps }: MoneyProps) {
  const negative = minor < 0;
  return (
    <Text
      component="span"
      // `tabular-nums` so a column of amounts lines up on the decimal point. Without it, a
      // proportional font makes 1,000.00 and 999.00 different widths and the column looks broken.
      style={{ fontVariantNumeric: 'tabular-nums' }}
      {...(negative && emphasiseNegative ? { c: 'oklch(var(--mx-danger))' } : {})}
      {...textProps}
    >
      {formatMoney(minor, currency)}
    </Text>
  );
}
